import './App.scss';
import { useEffect, useState } from 'react';
import Text from './components/Text';
import Choice from './components/Choice';
import ProgressBar from './components/ProgressBar';
import Form from './components/Form.js';
import Number from './components/Number.js';
import SubmitPage from './components/SubmitPage';
import OrderList from './components/OrderList.js';
import Content from './components/Content.js';
import Gallery from './components/Gallery.js';
import Grid from './components/Grid.js';
import { useTranslation } from 'react-i18next';

function App ({ theInsights, setShow, handleProgressSubmit, stepQuestion, embedded, embeddedPanelHeight }) {
	const { t } = useTranslation('survey');

	// Set the data.
	// const data = theInsights.structure[1].props.steps;
	// Note; setting the data like this due to the change to the json.
	// We may be able to revert to the above if the old style is definitely being used.
	const data = theInsights.structure.filter((str) => {
		if (str.view === 'steps' || str.view === 'questions' || str.view === '') {
			return str.view;
		}
	})[0].props[stepQuestion];

	// Set the conts for the various options.
	const [questions, setQuestions] = useState([]);
	const [choicesSelected, setChoicesSelected] = useState({
		choices: [],
	});
	const [responses, setResponses] = useState([]);
	const [submit, setSubmit] = useState(false);
	const [start, setStart] = useState(false);
	const [currentQuestion, setCurrentQuestion] = useState(0);
	const [text, setText] = useState('');
	const [progress, setProgress] = useState(0);
	const [number, setNumber] = useState('');
	const [list, setList] = useState(null);
	const [unordered, setUnordered] = useState(null);
	const [skipList, setSkipList] = useState([]);
	const [form, setForm] = useState({});
	const [grid, setGrid] = useState({});
	// Set the current question number string
	let questionNumber = 'Question ' + [currentQuestion + 1];
	if (data[currentQuestion].title) {
		questionNumber = data[currentQuestion].title;
	} else if (data[currentQuestion].type.toLowerCase() === 'content') {
		questionNumber = 'Info ' + [currentQuestion + 1];
	}
	let errorSelector = 'q' + [currentQuestion + 1] + 'error';
	let skippable = true;
	let backable = true;

	// Setting respoonses with empty properties as question and value as empty string
	useEffect(() => {
		setResponses(data.map((data) => ({
			[`${data.title}`]: '',
		})));
	}, []);

	// Any changes on progress
	useEffect(() => {
		// Update the height for embedded surveys
		if (embedded) {
			embeddedPanelHeight('app1');
		}
	}, [progress]); // <-- here put the parameter to listen, react will re-render component when your state will be changed

	function checkConditionalQuestion (directionToGo) {
		// Initial variables
		let zeroIndexedQuestions = data.length - 1;
		let checkNextQuestion = true;
		let questionToCheck = currentQuestion + 1;
		if (directionToGo === 'back') {
			questionToCheck = currentQuestion - 1;
		}

		// If there is a next question do the loops
		if (data[questionToCheck]) {
			do {
				// If the next question is higher than the total break out of the loop & go to the submit page.
				if (questionToCheck > zeroIndexedQuestions) {
					checkNextQuestion = false;
					break;
				}

				// Check if there's any conditionals on this questions
				if (data[questionToCheck].conditional) {
					let conditionalGroups = data[questionToCheck].conditional.conditionGroups;
					let anyGroupPass = false;

					// If going forward delete responses for future conditionals.
					// So if a user goes | [A] > A1 > A2 > back > back > [B] | then the A1/A2 answers will be removed from the array
					if (directionToGo === 'forward') {
						responses[questionToCheck] = { [`${data[questionToCheck].title}`]: '' };
						setResponses({ ...responses });
					}

					// Loop through the groups, quit if any have all of their conditions pass.
					do {
						for (var indexGroup = 0; indexGroup < conditionalGroups.length; indexGroup++) {
							let conditions = conditionalGroups[indexGroup].conditions;
							let allConditionPass = true; // Note: the assumption is the conditions will pass.

							// Then loop through the conditions on that group
							for (var indexCondition = 0; indexCondition < conditions.length; indexCondition++) {
								let questionID = conditions[indexCondition].question;
								let conditionStatus = conditions[indexCondition].conditionStatus;
								let acceptableAnswers = conditions[indexCondition].answers;

								// Find the question by the id, we need this to find the key rather than id as that's used in the responses array.
								let thisQuestion = data.filter(function (question) {
									return question.id === questionID;
								});
								let progressKey = thisQuestion[0].key;
								let currentResponses;

								// Check if responses is an object or array, it should be an object but can be an array when empty.
								if (typeof responses === 'object' && responses !== null) {
									currentResponses = Object.values(responses);
								} else {
									currentResponses = responses;
								}

								// Get the answers given for the condition question and create an array of answers both give and in the condition
								let answersForThisQuestion = currentResponses.find(obj => obj.hasOwnProperty(progressKey));
								let overLappingAnswers = acceptableAnswers.filter(function (item) {
									return answersForThisQuestion[progressKey].includes(item);
								});

								// If there aren't answers and it's set to show, or there are and it's set to hide then the condition doesn't pass
								// Note: the double negative here can be a bit counter intuitive
								if (
									(!overLappingAnswers.length && conditionStatus === 'show') ||
									(overLappingAnswers.length && conditionStatus === 'hide')
								) {
									allConditionPass = false;
								}
							}
							// If all the conditions have pass then mark the group as passing and stop the loop
							// If not the loop will go to the next condition loop.
							if (allConditionPass) {
								anyGroupPass = true;
								checkNextQuestion = false;
								break;
							}
						}
					} while (anyGroupPass = false);
				} else {
					// There aren't any conditions on this question, this will stop the loop.
					checkNextQuestion = false;
					continue;
				}

				// If another questions needs to be checked (ie this one didn't pass the conditional)
				// increase or decrease questionToCheck as needed and loop again.
				if (checkNextQuestion) {
					// Try the next question
					if (directionToGo === 'back') {
						questionToCheck--;
					} else {
						questionToCheck++;
					}
				}
			} while (checkNextQuestion);
		}
		return questionToCheck;
	}

	// to go to next question
	const progressToNextQuestion = () => {
		let zeroIndexedQuestions = data.length - 1;
		let nextQuestion = checkConditionalQuestion('forward');

		// Set current question
		if (nextQuestion <= zeroIndexedQuestions) {
			setCurrentQuestion(nextQuestion);
		}

		// enable submit button
		if (nextQuestion > zeroIndexedQuestions) {
			setSubmit(true);
		}

		// progress bar
		setProgress(prevProgress => {
			const newProgress = prevProgress + progressBarLength;
			return newProgress > 100 ? 100 : newProgress;
		});
	};

	// Set the pseudo-vh var
	var vh = window.innerHeight * 0.01;
	document.documentElement.style.setProperty('--vh', vh + 'px');
	// Reset on resize
	window.addEventListener('resize', bpresize, false);
	function bpresize () {
		// Update the pseudo-vh
		vh = window.innerHeight * 0.01;
		document.documentElement.style.setProperty('--vh', vh + 'px');
	}

	const showError = () => {
		// Show the error / Move the p into the div
		if (document.querySelector('#' + errorSelector)) {
			document.querySelector('#' + errorSelector).append(document.querySelector('#' + errorSelector + '-content'));
		}
		// Scroll into view
		document.querySelector('.question__error').scrollIntoView({
			behavior: 'smooth',
		});
		// And focus on the first aria-invalid element
		if (document.querySelector('.question__question [aria-invalid="true"]')) {
			// console.log(document.querySelector('.question__question [aria-invalid="true"]'));
			document.querySelector('.question__question [aria-invalid="true"]').focus();
		}
		// If embedded resize the iframe
		if (embedded) {
			embeddedPanelHeight('error');
		}
	};

	const hideError = () => {
		// Hide the error by removing the p
		if (document.querySelector('#' + errorSelector)) {
			document.querySelector('#' + errorSelector).parentNode.append(document.querySelector('#' + errorSelector + '-content'));
		}
	};

	// Email validation from https://stackoverflow.com/a/46181
	const validateEmail = (email) => {
		return email.match(
			// eslint-disable-next-line
			/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
		);
	};

	// Next button
	const nextQuestion = () => {
		// Set the current type as a variable.
		let currentType = data[currentQuestion].type;

		// Switch based on lower case to avoid issues.
		switch (currentType.toLowerCase()) {
			// Numbers [range slider]
			case 'number':
				const numberValue = { [`${data[currentQuestion].title}`]: number };
				responses[currentQuestion] = numberValue;
				setResponses({ ...responses });
				break;

			// Paragraph text
			case 'paragraph':
				const paragraphValue = { [`${data[currentQuestion].title}`]: text };
				if (data[currentQuestion].required && text === '' || text.length > data[currentQuestion].max_characters) {
					// If not enough choices have been selected return false so it doesn't go to the next question
					showError();
					return false;
				} else {
					hideError();
				}
				responses[currentQuestion] = paragraphValue;
				setResponses({ ...responses });
				break;

			// Order [choices with drag-and-drop]
			case 'order':
				if (list) {
					const listValue = { [`${data[currentQuestion].title}`]: list };
					responses[currentQuestion] = listValue;
					setResponses({ ...responses });
					if (skipList[currentQuestion]) {
						delete skipList[currentQuestion];
					}
				}
				break;

			// Form with multiple inputs.
			case 'form':
				let hasErrors = false;
				const hideAllErrors = () => {
					document.querySelectorAll('.single-error, single-error--required, single-error--format').forEach(function (el, i) {
						el.classList.remove('single-error', 'single-error--required', 'single-error--format');
					});
				};
				// Hide any exisiting errors
				hideAllErrors();
				// Loop through the form fields...
				for (var i = data[currentQuestion].fields.length - 1; i >= 0; i--) {
					if (data[currentQuestion].fields[i].label) {
						let currentID = data[currentQuestion].fields[i].label.toLowerCase().replace(/\s/g, '-').replace(/\W/g, '-');
						// Validate if any are required
						if (data[currentQuestion].fields[i].required) {
							if (!form[data[currentQuestion].fields[i].key]) {
								document.querySelector('#' + currentID).classList.add('single-error', 'single-error--required');
								hasErrors = true;
								document.querySelector('#' + currentID).setAttribute('aria-invalid', true);
								document.querySelector('#' + currentID).setAttribute('aria-describedby', currentID + 'error');
							} else {
								document.querySelector('#' + currentID).removeAttribute('aria-invalid');
								document.querySelector('#' + currentID).removeAttribute('aria-describedby');
							}
						}
						// Validate if it's an email address
						if (data[currentQuestion].fields[i].type === 'email') {
							let emailValue = form[data[currentQuestion].fields[i].key];
							if (emailValue && !validateEmail(emailValue)) {
								document.querySelector('#' + currentID).classList.add('single-error', 'single-error--format');
								hasErrors = true;
								document.querySelector('#' + currentID).setAttribute('aria-invalid', true);
								document.querySelector('#' + currentID).setAttribute('aria-describedby', currentID + 'error');
							} else {
								document.querySelector('#' + currentID).removeAttribute('aria-invalid');
								document.querySelector('#' + currentID).removeAttribute('aria-describedby');
							}
						}
					}
				}

				// If there are any errors...
				if (hasErrors) {
					// Show the main error.
					showError();
					return false;
				} else {
					// ...or hide all errors
					hideError();
					hideAllErrors();
				}

				responses[currentQuestion] = form;
				setResponses({ ...responses });
				break;

			// Grid with multiple inputs.
			case 'grid':
				let hasGridErrors = false;
				const hideAllGridErrors = () => {
					document.querySelectorAll('.grid-error').forEach(function (el, i) {
						el.classList.remove('grid-error');
					});
				};
				// Hide any exisiting errors
				hideAllGridErrors();
				// Loop through the form fields...
				for (var j = data[currentQuestion].topics.length - 1; j >= 0; j--) {
					let currentName = (data[currentQuestion].key + ' - ' + data[currentQuestion].topics[j].key);
					let currentID = currentName.toLowerCase().replace(/\s/g, '-').replace(/\W/g, '-') + 'error';
					// Validate if any are required
					if (data[currentQuestion].topics[j].required) {
						if (!grid[currentName]) {
							document.querySelector('#' + currentID).classList.add('grid-error');
							hasGridErrors = true;
							document.querySelector('#' + currentID).setAttribute('aria-invalid', true);
							document.querySelector('#' + currentID).setAttribute('aria-describedby', currentID + 'error');
						} else {
							document.querySelector('#' + currentID).removeAttribute('aria-invalid');
							document.querySelector('#' + currentID).removeAttribute('aria-describedby');
						}
					}
				}

				// If there are any errors...
				if (hasGridErrors) {
					// Show the main error.
					showError();
					return false;
				} else {
					// ...or hide all errors
					hideError();
					hideAllGridErrors();
				}

				responses[currentQuestion] = grid;
				setResponses({ ...responses });
				break;

			// Choices, either radio (max answer = 1), or checkboxes (max answer > 1)
			case 'choice':
				let min = 0;
				if (data[currentQuestion].min_answers) {
					min = data[currentQuestion].min_answers;
				} else if (data[currentQuestion].required) {
					min = 1;
				}
				const choiceValue = { [`${data[currentQuestion].key}`]: choicesSelected.choices };
				if (choicesSelected.choices.length < min) {
					// If not enough choices have been selected return false so it doesn't go to the next question
					showError();
					return false;
				} else {
					hideError();
				}
				responses[currentQuestion] = choiceValue;
				setResponses({ ...responses });
				break;

			default:
				// Do nothing by default.
		}

		// Progress to the next question, call this after the data above so validation can be done
		progressToNextQuestion();

		// Reset the text, number and choices
		setText('');
		setNumber('');
		setChoicesSelected({
			choices: [],
		});

		// if next question is a form set responses to form
		if (data[(currentQuestion + 1)] && data[(currentQuestion + 1)].type === 'form') {
			setForm(responses[currentQuestion + 1]);
		}

		// if next question is a grid set responses to grid
		if (data[(currentQuestion + 1)] && data[(currentQuestion + 1)].type === 'grid') {
			setGrid(responses[currentQuestion + 1]);
		}

		// Focus on back button so you're at the start of the new screen
		if (document.querySelector('.survey__back button')) {
			document.querySelector('.survey__back button').focus();
		}

		// Scroll to top
		document.body.scrollTop = document.documentElement.scrollTop = 0;
	};

	// Previous button
	const prevQuestion = () => {
		let zeroIndexedQuestions = data.length - 1;
		let questionToGoTo = checkConditionalQuestion('back');

		if (progress <= 0 || currentQuestion <= 0) {
			setShow(false);
			// console.log('app');
			handleProgressSubmit('Welcome');
		}

		if (questionToGoTo <= zeroIndexedQuestions) {
			setCurrentQuestion(questionToGoTo);
		}

		// progress bar
		setProgress(prevProgress => {
			const newProgress = prevProgress - progressBarLength;
			return newProgress < 0 ? 0 : newProgress;
		});

		// Hide the error if someone's gone backwards
		const errorSelector = 'q' + [currentQuestion + 1] + 'error';
		if (document.querySelector('#' + errorSelector)) {
			document.querySelector('#' + errorSelector).parentNode.append(document.querySelector('#' + errorSelector + '-content'));
		}

		if (data[questionToGoTo]) {
			// [TODO] Make this a switch?
			// Set responses to text
			if (data[questionToGoTo].type === 'paragraph') {
				const Ans = responses[questionToGoTo];
				for (var paraKey in Ans) {
					setText(Ans[paraKey]);
				}
			}
			// Set responses to number
			if (data[questionToGoTo].type === 'number') {
				const Ans = responses[questionToGoTo];
				for (var numKey in Ans) {
					setNumber(Ans[numKey]);
				}
			}

			// Set responses to list/order
			if (data[questionToGoTo].type === 'order' && responses[currentQuestion]) {
				setList(Object.values(responses[questionToGoTo])[0]);
			}

			// Set responses to form
			if (data[questionToGoTo].type === 'form' && responses[currentQuestion]) {
				setForm(responses[questionToGoTo]);
				// console.log(responses[currentQuestion], 'cq');
			}

			// Set responses to grid
			if (data[questionToGoTo].type === 'grid' && responses[currentQuestion]) {
				setGrid(responses[questionToGoTo]);
				// console.log(responses[currentQuestion], 'cq');
			}
		}

		if (data[currentQuestion].type === 'choice' && choicesSelected) {
			const choiceValue = { [`${data[currentQuestion].key}`]: choicesSelected.choices };
			responses[currentQuestion] = choiceValue;
			setResponses({ ...responses });
		}
	};

	// Skip question
	const skipQuestion = () => {
		progressToNextQuestion();

		// Set the current type as a variable.
		let currentType = data[currentQuestion].type;

		// Switch based on lower case to avoid issues.
		switch (currentType.toLowerCase()) {
			// Numbers [range slider]
			case 'number':
				const numberValue = { [`${data[currentQuestion].title}`]: '' };
				responses[currentQuestion] = numberValue;
				setResponses({ ...responses });
				break;

			// Paragraph text
			case 'paragraph':
				const paragraphValue = { [`${data[currentQuestion].title}`]: '' };
				responses[currentQuestion] = paragraphValue;
				setResponses({ ...responses });
				break;

			// Choices, either radio (max answer = 1), or checkboxes (max answer > 1)
			case 'choice':
				const choiceValue = { [`${data[currentQuestion].key}`]: '' };
				responses[currentQuestion] = choiceValue;
				setResponses({ ...responses });
				break;

			default:
				// Do nothing by default.
		}

		if (currentType.toLowerCase() === 'order') {
			const value1 = { [`${data[currentQuestion].title}`]: '' };
			skipList[currentQuestion] = value1;
			setSkipList({ ...skipList });
		}
		if (currentType.toLowerCase() === 'form' || currentType.toLowerCase() === 'grid') {
			Object.keys(responses[currentQuestion]).forEach(key => responses[currentQuestion][key] = '');
		}
	};

	// list handler
	const listOrderChange = (value) => {
		setList(value);
	};

	// progress bar
	const progressIntialVal = 0;
	const progressBarLength = (100 / data.length);
	useEffect(() => {
		setProgress(progressIntialVal > 100 ? 100 : progressIntialVal < 0 ? 0 : progressIntialVal);
	}, [progressIntialVal]);

	useEffect(() => {
		handleProgressSubmit(questionNumber);
	}, [questionNumber]);

	// set initial values to responses from data
	useEffect(() => {
		// set initial list to order
		if (data[currentQuestion].type === 'order') {
			const ArrayList = data[currentQuestion].answers.map((item) => item.label);
			setUnordered(ArrayList);
		}
		// set initial properties with empty values to form
		if (data[currentQuestion].type === 'form') {
			const isNotEmpty = Boolean(Object.values(responses[currentQuestion]).join(''));
			if (!isNotEmpty) {
				data[currentQuestion].fields.forEach((fields, i) => {
					setForm(form => ({ ...form, [`${fields.label}`]: '' }));
				});
			}
		}
		// set initial properties with empty values to form
		if (data[currentQuestion].type === 'grid') {
			const isNotEmpty = Boolean(Object.values(responses[currentQuestion]).join(''));
			if (!isNotEmpty) {
				// data[currentQuestion].fields.forEach((fields, i) => {
				// 	setGrid(grid => ({ ...grid, [`${fields.label}`]: '' }));
				// });
			}
		}
	}, [currentQuestion]);

	// Setting order questions
	useEffect(() => {
		if (responses[currentQuestion] && data[currentQuestion].type === 'order') {
			setList(unordered);
			if (Object.values(responses[(currentQuestion)])[0] != '') { // [TODO] should this be `!==` ?
				setList(Object.values(responses[(currentQuestion)])[0]);
			}
		}
	}, [unordered]);

	// Stop skipping if question is required / has a required sub question.
	// Or if min answers is more than 0
	if (
		JSON.stringify(data[currentQuestion]).indexOf('"required":true') > -1 ||
		data[currentQuestion].min_answers > 0
	) {
		skippable = false;
	}

	// Check the wrapper heights.
	if (embedded && currentQuestion === 0) {
		embeddedPanelHeight('app initial');
		backable = false;

		// The initial one seems to mis-calculate ([TODO] something to do with rendering/time of this call?)
		// So add a loop to check
		setTimeout(() => {
			embeddedPanelHeight('embed loop');
			// console.warn('looop');
		}, 500);
	}

	return (
		<div className="App">
			{ !submit ? (
				<div className="survey">
					<header className="survey__header">
						<div className="survey__back">
							{(backable) ? <button className="plain" onClick={prevQuestion}>{t('survey.generic.Back')}</button> : ''}
						</div>
						<div className="survey__skip">
							{(skippable) ? <button className="plain" onClick={skipQuestion}>{t('survey.generic.Skip')}</button> : ''}
						</div>
					</header>
					{theInsights.structure.map((str) => {
						if (str.view === 'welcome') {
							return (
								<div className="survey__branding">
									{(str.props.logo_url) ? <img src={str.props.logo_url} alt=""/> : ''}
									<div>{str.props.title}</div>
								</div>
							);
						}
					})}
					<div className="survey__progress-outer">
						<div className="survey__progress">
							<ProgressBar progress={progress} />
						</div>
					</div>

					{(() => {
						// Set the current type as a variable.
						let currentType = data[currentQuestion].type;

						// Switch based on lower case to avoid issues.
						switch (currentType.toLowerCase()) {
							// Numbers [range slider]
							case 'number':
								return (<Number data={data} currentQuestion={currentQuestion} responses={responses} setResponses={setResponses} number={number} setNumber={setNumber}/>);
								break;

							// Paragraph text
							case 'paragraph':
								if (responses[currentQuestion]) {
									return (<Text data={data} currentQuestion={currentQuestion} nextQuestion={nextQuestion} setText={setText} text={text} responses={responses}/>);
								}
								break;

							// content
							case 'content':
								if (responses[currentQuestion]) {
									return (<Content data={data} currentQuestion={currentQuestion} nextQuestion={nextQuestion} responses={responses}/>);
								}
								break;

							// content
							case 'gallery':
								if (responses[currentQuestion]) {
									return (<Gallery data={data} currentQuestion={currentQuestion} nextQuestion={nextQuestion} responses={responses}/>);
								}
								break;

							// Order [choices with drag-and-drop]
							case 'order':
								if (list) {
									return (<OrderList data={data} currentQuestion={currentQuestion} responses={responses} setResponses={setResponses} list={list} setList={setList} listOrderChange={listOrderChange} unordered={unordered}/>);
								}
								break;

							// Form with multiple inputs.
							case 'form':
								return (<Form data={data} currentQuestion={currentQuestion} responses={responses} setResponses={setResponses} form={form} setForm={setForm} />);
								break;

							// Grid.
							case 'grid':
								return (<Grid data={data} currentQuestion={currentQuestion} responses={responses} setResponses={setResponses} grid={grid} setGrid={setGrid} />);
								break;

							// Choices, either radio (max answer = 1), or checkboxes (max answer > 1)
							case 'choice':
								return (<Choice data={data} currentQuestion={currentQuestion} responses={responses} choicesSelected={choicesSelected} setChoicesSelected={setChoicesSelected}/>);
								break;

							default:
								// Do nothing by default.
						}
					})()}
					<footer className="survey__footer">
						<button onClick={nextQuestion}>{t('survey.generic.Next')}</button>
					</footer>
				</div>)
				: (
					<SubmitPage insights={theInsights} responses= {responses} skipList={skipList} setResponses={setResponses} setSubmit={setSubmit} handleProgressSubmit={handleProgressSubmit} currentQuestion={currentQuestion} embedded={embedded} embeddedPanelHeight={embeddedPanelHeight}/>
				)
			}
		</div>
	);
}

export default App;
